import { ClockIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import type { Color } from 'stories/types'
import { InputConvert, MAX_INPUT_DATE, MIN_INPUT_DATE } from 'utils'

const COMPLETE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Native date inputs emit a full date on the first year digit (10/02/2026 becomes 0002-10-02, then
 * 0020, 0202, and only then 2026). Those intermediate years are real values, so a parent that saves
 * on change persists 0002 and can disable the field before the rest of the year is typed.
 */
const isCompleteInputDate = (value: string): boolean =>
  value === '' || (COMPLETE_DATE_RE.test(value) && value >= MIN_INPUT_DATE && value <= MAX_INPUT_DATE)

interface InputProps {
  /**
   * What background color to use
   */
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is autofocus
   */
  /**
   * Is readOnly
   */
  readOnly?: boolean
  autoFocus?: boolean
  /**
   * Type of Input
   */
  type?: string | undefined
  /**
   * Title of Input
   */
  title?: string
  /**
   * Placeholder of Input
   */
  placeholder?: string
  /**
   * Name of Input
   */
  name?: string
  /**
   * Value of Input
   */
  value?: string | number
  /**
   * Error of Input
   */
  error?: string
  /**
   * Custom class name
   */
  className?: string
  /**
   * Is has icon
   */
  hasIcon?: boolean
  /**
   * Required
   */
  required?: boolean
  /**
   * Icon component
   */
  icon?: string | JSX.Element | null
  /**
   * onChange
   */
  history?: boolean
  /**
   * Icon component
   */
  onChange: (e: any) => void // string | React.ChangeEvent<HTMLInputElement>) => void
  showHistory?: () => void
  onKeyPress?: (e: any) => void
  onKeyDown?: (e: any) => void
  onBlur?: (e: any) => void
}

/**
 * Primary UI component for user interaction
 */
export const Input = ({
  color = 'sky',
  disabled = false,
  readOnly = false,
  autoFocus = false,
  type = 'text',
  title = '',
  placeholder = ' ',
  name = '',
  value = '',
  error = '',
  className = '',
  hasIcon = false,
  icon = null,
  history = false,
  required = false,
  onChange = () => {},
  showHistory = () => {},
  onBlur,
  onKeyDown,
  ...props
}: InputProps) => {
  const classNames = [
    'block',
    'rounded-xl',
    'py-2',
    'px-3',
    'w-full',
    'text-sm',
    'text-gray-900',
    // soft-filled (Material-3): tinted at rest, turns white on focus
    disabled ? 'bg-slate-100' : 'bg-white',
    'border',
    'border-slate-300',
    'appearance-none',
    'transition',
    disabled || readOnly ? '' : 'hover:border-slate-400',
    'dark:text-white',
    'dark:border-gray-600',
    'focus:outline-none',
    'focus:bg-white',
    'focus:ring-2',
    'focus:ring-shade-blue/20',
    'focus:border-shade-blue',
    'peer',
    hasIcon && icon ? 'pl-10' : '',
  ]

  if (value === null) value = ''
  value = InputConvert({ type }, value)

  const inputRef = useRef<HTMLInputElement>(null)
  /** True after a keystroke, until blur. A calendar pick does not set it. */
  const dateFromKeyboard = useRef(false)
  /** Date typed but not yet echoed by the parent. Keeps the control from snapping back mid-save. */
  const [heldDate, setHeldDate] = useState<string | null>(null)

  useEffect(() => {
    if (heldDate !== null && `${value || ''}` === heldDate) setHeldDate(null)
  }, [heldDate, value])

  // A date field stays uncontrolled. Pushing each keystroke back through `value` resets Chrome's
  // month/day/year segments, so typing 10022026 never lands. The parent hears only a finished date.
  useEffect(() => {
    if (type !== 'date' || !inputRef.current) return
    if (document.activeElement === inputRef.current) return
    if (heldDate !== null && `${value || ''}` !== heldDate) return
    const next = `${value || ''}`
    if (inputRef.current.value !== next) inputRef.current.value = next
  }, [heldDate, type, value])

  const publishDate = (next: string) => {
    if ((next || '') === `${value || ''}` || heldDate === next) return
    setHeldDate(next)
    onChange(next)
  }

  const handleChange = (e: any) => {
    const nextValue = e.target.value as string
    if (type === 'date') {
      // A filled date turns "05" into 05/02/2026 immediately. That is still the middle of typing,
      // so save it on blur instead of disabling the field. A calendar pick has no keystroke.
      if (e.target.validity?.badInput || !isCompleteInputDate(nextValue) || dateFromKeyboard.current) return
      publishDate(nextValue)
      return
    }
    onChange(nextValue)
  }

  const handleKeyDown = (e: any) => {
    if (type === 'date') {
      dateFromKeyboard.current = true
      if (e.key === 'Enter') {
        const next = e.currentTarget.value || ''
        if (!e.currentTarget.validity?.badInput && isCompleteInputDate(next)) publishDate(next)
      }
    }
    onKeyDown && onKeyDown(e)
  }

  const handleBlur = (e: any) => {
    if (type === 'date') {
      const keyed = dateFromKeyboard.current
      dateFromKeyboard.current = false
      const typed = e.target.value || ''
      if (e.target.validity?.badInput || !isCompleteInputDate(typed)) {
        e.target.value = value || ''
      } else if (keyed) publishDate(typed)
    }
    onBlur && onBlur(e)
  }

  return (
    <div className="input-container mb-4">
      <div className={`group relative z-0 w-full ${className} ${readOnly ? 'cursor-not-allowed' : ''}`}>
        {hasIcon && icon && typeof icon === 'string' && (
          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
            <img src={icon} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
        {hasIcon && icon && typeof icon !== 'string' && (
          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">{icon}</div>
        )}
        <input
          type={type}
          name={name}
          id={name}
          className={classNames.join(' ')}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          ref={inputRef}
          required={required}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          // Bounded to the same window InputValidate accepts, so a mis-keyed two-digit year
          // (8/26/26 -> 0026-08-26) is refused by the field rather than silently carried.
          min={type === 'date' ? MIN_INPUT_DATE : undefined}
          max={type === 'date' ? MAX_INPUT_DATE : undefined}
          {...(type === 'date' ? { defaultValue: value || '' } : { value })}
          {...props}
        />
        {history && (
          <span className="ml-1 hidden group-hover:inline absolute right-2 -top-4" onClick={() => showHistory()}>
            <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
          </span>
        )}
        <label
          htmlFor={name}
          className={`absolute top-[10.25px] text-xs text-gray-700 dark:text-gray-400 duration-300 transform -translate-y-6 top-3 origin-[0] peer-focus:left-0 peer-focus:text-${color}-700 peer-focus:dark:text-${color}-700 peer-placeholder-shown:text-sm peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:text-xs peer-focus:-translate-y-6 peer-placeholder-shown:leading-3 ml-2 px-1 rounded ${
            disabled && value?.toString().length === 0 ? '' : 'bg-white'
          } peer-placeholder-shown:-z-10 peer-focus:z-10 ${hasIcon && icon ? 'pl-8' : ''} peer-focus:pl-1 left-0`}
        >
          {title}
          {required && '*'}
        </label>
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-xs pl-1">{error}</p>}
    </div>
  )
}
